import React, { useEffect, useRef, useState } from 'react'

import {
  isEmpty,
  noop,
  PartialShallow,
  some,
  map as mapSpecificKey,
} from 'lodash'
import {
  Control,
  Controller,
  FieldErrorsImpl,
  FieldValues,
  GlobalError,
  Path,
} from 'react-hook-form'

import {
  SvgClearCross,
  SvgDropArrow,
  SvgFilledCross,
} from '../../assets/images'
import { NAMESPACE } from '../../utils'

interface OutsideClickHandlerProps {
  ref: React.RefObject<HTMLElement>
  handler: () => void
}

const useOutsideClick = ({ ref, handler }: OutsideClickHandlerProps) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, handler])
}

interface DropdownType<T, W = string> {
  value: W
  label: string
  disabled?: boolean
  [key: string]: T | string | boolean | undefined | W
}

interface OptionContainerPropsType<T, W> {
  option: DropdownType<T, W>
  handleValueSelect: (e: DropdownType<T, W>) => void
  isSelected: boolean
}

const OptionContainer = <T, W>({
  option,
  handleValueSelect,
  isSelected,
}: OptionContainerPropsType<T, W>) => {
  const handleSelected = () => {
    if (!isEmpty(option)) {
      handleValueSelect(option)
    }
  }
  return (
    <li
      className={`p-3 border-t border-border-100 hover:bg-primary-25 flex items-center  pointer-events-auto  cursor-pointer w-full text-black `}
      onClick={handleSelected}
    >
      <input
        type="checkbox"
        className="w-[1.125rem] h-[1.125rem] accent-secondary-500"
        checked={isSelected}
        onChange={noop}
      />

      <span className="max-w-[calc(100%-0.938rem)] truncate pl-3">
        {option?.label}
      </span>
    </li>
  )
}

interface OptionBoxPropsType<T, W> {
  options: DropdownType<T, W>[]
  excludedParentRef: React.RefObject<HTMLElement>
  handleValueSelect: (e: DropdownType<T, W>) => void
  handleOptionFalse: () => void
  toggleOption: () => void
  isOpenOptions: boolean
  selectedOption: DropdownType<T, W>[]
}

const OptionBox = <T, W>({
  options,
  excludedParentRef,
  handleValueSelect,
  handleOptionFalse,
  isOpenOptions,
  selectedOption,
}: OptionBoxPropsType<T, W>) => {
  const dropdownOptionRef = useRef<HTMLDivElement>(null)
  useOutsideClick({
    ref: excludedParentRef,
    handler: handleOptionFalse,
  })
  return (
    <div
      ref={dropdownOptionRef}
      className={` font-normal overflow-y-auto absolute mt-[0.006rem] mr-5 w-full rounded-b  z-[9999] border-r border-l border-b  bg-accent-0 max-h-[12.375rem]   group-hover:border-t-0 
         ${isOpenOptions ? 'border-secondary-500' : ' border-accent-600'}
        group-hover:border-secondary-500`}
    >
      <div>
        <ul>
          {options.map((option: DropdownType<T, W>, index: number) => (
            <OptionContainer
              key={`${option?.value}_${index}`}
              option={option}
              handleValueSelect={handleValueSelect}
              isSelected={mapSpecificKey(selectedOption, 'value').includes(
                option?.value
              )}
            />
          ))}
        </ul>
      </div>
    </div>
  )
}

interface FieldErrors<U extends FieldValues> {
  root?: Record<string, GlobalError> & GlobalError
  [key: string]: FieldErrorsImpl<U> | GlobalError | undefined
}

interface MultiSelectPropTypes<T, U extends FieldValues, W> {
  label: string
  options: DropdownType<T, W>[]
  name: string
  defaultValue?: W[]
  disabled?: boolean
  required?: boolean
  onChange: (value: DropdownType<T, W>[], name: string) => void
  placeholder?: string
  className?: string
  errors?: FieldErrors<U>
  noOfChips?: number
  dataQaId?: string
}

export const MultiSelect = <T, U extends FieldValues, W>({
  label = '',
  name = '',
  options,
  defaultValue,
  disabled = false,
  required = false,
  onChange = noop,
  placeholder = 'Select Option',
  className = '',
  errors,
  noOfChips = 1,
  dataQaId,
}: MultiSelectPropTypes<T, U, W>) => {
  const [isOpenOptions, setIsOpenOptions] = useState(false)
  const [selectedOption, setSelectedOption] = useState<DropdownType<T, W>[]>([])
  const dropdownRef = useRef<HTMLDivElement>(null)
  const ListArr = new Array(noOfChips).fill('1')

  const toggleOption = () => {
    setIsOpenOptions((prevOption) => !prevOption)
  }

  const handleOptionFalse = () => {
    setIsOpenOptions(false)
  }
  const handleValueSelect = (selectedValue: DropdownType<T, W>) => {
    if (
      mapSpecificKey(selectedOption, 'value').includes(selectedValue?.value)
    ) {
      const fieldIndex = mapSpecificKey(selectedOption, 'value').indexOf(
        selectedValue?.value
      )
      const slicedOption = [...selectedOption]
      slicedOption.splice(fieldIndex, 1)
      onChange(slicedOption, name)
      setSelectedOption([...slicedOption])
    } else {
      onChange([...selectedOption, selectedValue], name)
      setSelectedOption([...selectedOption, selectedValue])
    }
  }

  const clearAll = () => {
    setSelectedOption([])
    onChange([], name)
  }

  const clearChip = (index: number) => {
    const cloneSelectedValue = structuredClone(selectedOption)
    cloneSelectedValue.splice(index, 1)
    setSelectedOption(cloneSelectedValue)
    onChange(cloneSelectedValue, name)
  }

  useEffect(() => {
    if (!isEmpty(options) && defaultValue) {
      const selected = options?.filter(
        (option: DropdownType<T, W>) =>
          // defaultValue?.includes(option?.value)
          //   some(defaultValue,option)
          // we will be sending default values as option type? with label? No!
          // option.value
          some(defaultValue, option.value as PartialShallow<W>) // fix this.
      )
      setSelectedOption(selected || [])
    }
  }, [defaultValue, options])

  return (
    <div
      ref={dropdownRef}
      className={` group   relative w-full font-primary  ${className}   ${
        disabled ? ' cursor-not-allowed' : 'cursor-pointer select-none'
      }`}
      onClick={isEmpty(options) ? noop : disabled ? noop : noop}
      data-qa-id={dataQaId ?? ''}
    >
      <div className=" max-h-10  w-full box-border relative bg-accent-0 items-center rounded">
        {label ? (
          <div
            className={`translate-x-[0.875rem] translate-y-[-0.75rem] flex  text-base p-0 origin-top-left whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100%-1.75rem)] absolute left-0 top-0 z-[1] pointer-events-none ${
              disabled ? 'text-accent-300' : 'text-black'
            }`}
          >
            <label
              className={` font-normal block text-xs leading-1 font-primary whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100% - 1.5rem]  
               ${isOpenOptions ? 'text-secondary-500' : ''}
              ${
                disabled ? 'text-accent-300' : 'group-hover:text-secondary-500'
              }`}
              title={label}
            >
              {label}
            </label>
            {required ? (
              <span
                className={`ml-1 text-xs leading-1  ${
                  disabled ? 'text-accent-300' : 'text-error-500'
                }`}
              >
                *
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="z-[2]  pl-4 pr-2.5 pt-2 pb-2  relative">
          <div className="flex  items-center justify-between w-full  ">
            <div
              className={`font-light  max-w-[calc(100%-0.938rem)] w-full overflow-y-auto overflow-x-hidden no-scroll-display items-center h-[1.25rem] flex flex-wrap gap-1
                ${disabled ? 'text-accent-500' : ''}
            ${!isEmpty(selectedOption) ? 'text-black' : 'text-accent-300'}`}
            >
              {!isEmpty(selectedOption) ? (
                <>
                  {ListArr?.map((val, index) => (
                    <React.Fragment key={index + '' + val}>
                      {index < selectedOption.length ? (
                        <div className="bg-secondary-50 text-accent-500 flex justify-center items-center  py-0.5 px-2 mr-1 rounded-full max-w-[calc(100%-0.938rem)]">
                          <span className="leading-5 text-xs truncate max-w-[6.25rem] font-primary">
                            {selectedOption?.[index]?.label}
                          </span>
                          <div
                            className="p-[0.094rem]"
                            onClick={() => clearChip(index)}
                          >
                            <SvgFilledCross />
                          </div>
                        </div>
                      ) : null}
                    </React.Fragment>
                  ))}
                  {selectedOption?.length - ListArr?.length > 0 ? (
                    <div className="bg-secondary-50 text-accent-300 flex justify-center items-center  py-0.5 px-2 mr-1 rounded-full">
                      <span className="leading-5 text-xs truncate max-w-[9.375rem]">
                        {'+' + (selectedOption?.length - ListArr?.length)}
                      </span>
                    </div>
                  ) : null}
                </>
              ) : (
                <span
                  className="leading-5 text-xs truncate max-w-[calc(100%-0.938rem)] w-full"
                  onClick={toggleOption}
                >
                  {placeholder}
                </span>
              )}
            </div>
            {selectedOption?.length > 0 ? (
              <div className="p-1" onClick={disabled ? noop : clearAll}>
                <SvgClearCross />
              </div>
            ) : null}

            <div className="p-2" onClick={toggleOption}>
              <SvgDropArrow
                className={`transform transition-transform duration-200  ${
                  isOpenOptions ? 'rotate-180' : ''
                } ${disabled ? 'invisible' : ''}`}
              />
            </div>
          </div>
        </div>

        <fieldset
          aria-hidden="true"
          className={`text-left  border absolute ${
            label ? 'top-[-0.938rem]' : 'top-0'
          } bottom-0 left-0 right-0 px-2 pointer-events-none rounded border-solid overflow-hidden min-w-0 
                    border-border-150
                 
                    ${
                      isOpenOptions
                        ? 'border-b-0 rounded-b-none group-hover:border-b-0 group-hover:rounded-b-none border-secondary-500'
                        : ''
                    }
                    ${
                      disabled
                        ? 'border-accent-200 bg-accent-50'
                        : 'group-hover:border-secondary-500'
                    }
                    ${
                      !isEmpty(errors?.[name])
                        ? 'border-error-500'
                        : 'border-border-150'
                    }`}
        >
          {label ? (
            <legend className=" invisible flex float-none max-w-full select-none  p-1 box-border ">
              <span
                className={` font-normal  dropdown-label overflow-hidden whitespace-nowrap  text-ellipsis  
                        ${isOpenOptions ? 'text-secondary-500' : ''}
                        ${
                          disabled
                            ? 'text-accent-300'
                            : 'group-hover:text-secondary-500 '
                        }`}
              >
                {label}
              </span>
              {required ? (
                <span
                  className={`ml-1 text-xs leading-1  ${
                    disabled ? 'text-accent-300' : 'text-error-500'
                  }`}
                >
                  *
                </span>
              ) : null}
            </legend>
          ) : null}
        </fieldset>
      </div>

      {isOpenOptions ? (
        <OptionBox
          excludedParentRef={dropdownRef}
          handleValueSelect={handleValueSelect}
          handleOptionFalse={handleOptionFalse}
          toggleOption={toggleOption}
          options={options}
          isOpenOptions={isOpenOptions}
          selectedOption={selectedOption}
        />
      ) : null}

      {errors &&
        name &&
        errors?.[name] &&
        errors?.[name]?.['type'] === 'required' && (
          <span className="text-error-500 text-xxs font-primary font-extraLight">
            {NAMESPACE?.REQUIRED_FIELD}
          </span>
        )}
      {errors &&
        name &&
        errors?.[name] &&
        errors?.[name]?.['type'] !== 'required' && (
          <span className="text-error-500 text-xs">
            {errors?.[name]?.['message']?.toString()}
          </span>
        )}
    </div>
  )
}

interface MultiLookupMultiSelectDropDownPropTypes<T, U extends FieldValues, W> {
  control: Control<U>
  name: Path<U>
  isSearchable?: boolean
  errors: FieldErrors<U>
  onChange: (value: DropdownType<T, W>[], name: string) => void
  label?: string
  options: DropdownType<T, W>[]
  required?: boolean
  disabled?: boolean
  noOfChips?: number
  placeholder?: string
  dataQaId?: string
}

const MultiLookupMultiSelectDropDown = <T, U extends FieldValues, W>({
  control,
  name,
  errors,
  onChange,
  label = '',
  options = [],
  required,
  disabled = false,
  placeholder = '',
  noOfChips = 1,
  dataQaId,
}: MultiLookupMultiSelectDropDownPropTypes<T, U, W>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required }}
      disabled={disabled}
      render={({ field }) => (
        <MultiSelect
          disabled={field?.disabled}
          name={field?.name}
          label={label}
          errors={errors}
          defaultValue={field?.value}
          options={options}
          required={required}
          onChange={onChange}
          noOfChips={noOfChips}
          placeholder={placeholder}
          dataQaId={dataQaId}
        />
      )}
    />
  )
}

export default MultiLookupMultiSelectDropDown
